import React, { FC, Fragment } from 'react';
import { Popover, Transition } from '@headlessui/react';
import { CrossIcon, DownArrowIcon, PlusIcon } from 'assets/icons';
import { twMerge } from 'tailwind-merge';
import classNames from 'classnames';
import DropdownPopoverPanel from './DropdownPopoverPanel';

interface DropdownWithSearchProps {
  data: any[];
  dataKey: string;
  selectedDraftArray: string[];
  setSelectedDraftArray: (selectedDraftArray: string[]) => void;
  selectedArray: string[];
  setSelectedArray: (selectedArray: string[]) => void;
  searchText?: string;
  setSearchText?: (searchText: string) => void;
  dataIdKey: string;
  buttonPrimaryText: string;
  buttonSecondaryText: string;
  filterPlaceholderText: string;
  filterPlaceholderStyle?: string;
  searchPlaceHolderText?: string;
  searchInputStyle?: string;
  allowSearch: boolean;
  showResultsCount?: boolean;
  customClassName?: string;
  filterId: string;
  secondaryDataKey?: string;
  searchFilterId?: string;
  showCustomOptions?: boolean;
  customOption?: (option) => React.ReactNode;
  showSeparatorAfterFirstItem?: boolean;
}

const DropdownWithSearch: FC<DropdownWithSearchProps> = props => {
  const {
    data,
    dataKey,
    selectedDraftArray,
    setSelectedDraftArray,
    selectedArray,
    setSelectedArray,
    searchText = '',
    setSearchText = () => ({}),
    dataIdKey,
    buttonPrimaryText,
    buttonSecondaryText,
    filterPlaceholderText,
    filterPlaceholderStyle = '',
    searchPlaceHolderText = '',
    searchInputStyle = '',
    allowSearch,
    showResultsCount = true,
    customClassName,
    filterId,
    secondaryDataKey = '',
    searchFilterId,
    showCustomOptions = false,
    customOption,
    showSeparatorAfterFirstItem = false
  } = props;

  return (
    <div>
      <Popover className="relative">
        {({ open, close }) => (
          <>
            <Popover.Button
              className={twMerge(
                `shrink-0 rounded-md border 
                bg-primary px-3 py-2 text-sbase font-semibold text-primaryText  
                outline-none transition-all  duration-150 ease-linear focus:outline-none 
                ${selectedArray.length > 0 ? ' border-solid bg-secondaryBtn' : 'border-dashed'} `,
                classNames({ [customClassName]: customClassName })
              )}
              id={filterId}
              style={{ borderColor: selectedArray.length > 0 ? '#3876BB' : '#ACB5BD' }}
              onClick={() => {
                setSearchText('');
                setSelectedDraftArray(selectedArray);
              }}
              type="button">
              <div className={'flex shrink-0 flex-row items-center'}>
                <div>
                  {selectedArray.length ? (
                    <CrossIcon
                      onClick={e => {
                        e.stopPropagation();
                        close();
                        setSelectedArray([]);
                        setSelectedDraftArray([]);
                      }}
                    />
                  ) : (
                    <PlusIcon />
                  )}
                </div>
                <div id={`${filterId}-text`} className={'shrink-0 px-3 text-sbase'}>
                  {buttonPrimaryText}
                  {buttonSecondaryText !== '' && (
                    <div className="hidden lg:inline">
                      <span> {' | '} </span>
                      <span className={'text-secondary'}> {buttonSecondaryText} </span>
                    </div>
                  )}
                </div>
                <div>
                  <DownArrowIcon className={open ? 'rotate-180' : ''} />
                </div>
              </div>
            </Popover.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1">
              <Popover.Panel className="absolute z-40 mt-0.5 min-w-[200px] bg-primary px-4 shadow-md sm:px-0">
                <div
                  className={
                    (open ? 'block ' : 'hidden ') + 'z-50 pt-4  list-none rounded-md border border-[#ACB5BD] w-52'
                  }
                  style={{
                    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)'
                  }}>
                  <DropdownPopoverPanel
                    data={data}
                    dataKey={dataKey}
                    selectedDraftArray={selectedDraftArray}
                    setSelectedDraftArray={setSelectedDraftArray}
                    setSelectedArray={setSelectedArray}
                    searchText={searchText}
                    setSearchText={setSearchText}
                    dataIdKey={dataIdKey}
                    filterPlaceholderText={filterPlaceholderText}
                    filterPlaceholderStyle={filterPlaceholderStyle}
                    searchPlaceHolderText={searchPlaceHolderText}
                    searchInputStyle={searchInputStyle}
                    allowSearch={allowSearch}
                    showResultsCount={showResultsCount}
                    filterId={filterId}
                    secondaryDataKey={secondaryDataKey}
                    searchFilterId={searchFilterId}
                    close={close}
                    showCustomOptions={showCustomOptions}
                    customOption={customOption}
                    showSeparatorAfterFirstItem={showSeparatorAfterFirstItem}
                  />
                </div>
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>
    </div>
  );
};

export default DropdownWithSearch;
